
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, of } from 'rxjs';
import { PriceFileSimulationService } from './price-file-simulation.service';
import { formatDecimalWithCommas, formatStat, formatWholeNumberWithCommas } from '../utils/format.utils';
import { source } from '@angular-devkit/schematics';

@Injectable({
    providedIn: 'root'
})
export class SimulationService {

    private simulationResultsSubject = new BehaviorSubject<any[] | null>(null);
    public simulationResults$ = this.simulationResultsSubject.asObservable();

    private deepDiveFilterContextSubject = new BehaviorSubject<{ part_number_list: string[]; region_list: string[] }>({
        part_number_list: [],
        region_list: []
    });
    public deepDiveFilterContext$ = this.deepDiveFilterContextSubject.asObservable();

    private deepDiveDetailsResponseSubject = new BehaviorSubject<any | null>(null);
    public deepDiveDetailsResponse$ = this.deepDiveDetailsResponseSubject.asObservable();

    private newSimulationsSubject = new BehaviorSubject<any[]>([]);
    public newSimulations$ = this.newSimulationsSubject.asObservable();

    private simulationValues: any[] = [];
    private appliedFilters: any[] = [];
    private currentEntity: string = 'dn_pricing';
    private currentFilters: any[] = []; // Restored
    private lastBusinessId: string = '';
    private lastRunUuid: string = '';
    private lastRunAppliedFilters: any[] = [];
    private readonly RUN_SAVE_APPLIED_FILTER_DEBUG_KEY = '[RUN_SAVE_APPLIED_FILTER_DEBUG]';

    constructor(private pfss: PriceFileSimulationService) { }

    private logRunSaveAppliedFilters(stage: string, data: any): void {
        try {
            console.log(this.RUN_SAVE_APPLIED_FILTER_DEBUG_KEY, stage, JSON.parse(JSON.stringify(data)));
        } catch {
            console.log(this.RUN_SAVE_APPLIED_FILTER_DEBUG_KEY, stage, data);
        }
    }

    private normalizeEntityName(entity: string): string {
        const rawValue = String(entity || '').trim().toLowerCase();
        if (!rawValue) {
            return 'dn_pricing';
        }

        // Remove optional numeric code prefix like "035 - Base Price" or "030: DN Pricing"
        const withoutCodePrefix = rawValue.replace(/^\d+\s*[-:]\s*/, '').trim();
        const normalized = withoutCodePrefix
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');

        if (normalized === 'base_price') {
            return 'base_price';
        }
        if (normalized === 'dn_pricing') {
            return 'dn_pricing';
        }

        return normalized || 'dn_pricing';
    }

    setSimulationFilters(filters: any[]) {
        this.currentFilters = filters;
        // Normalize UI label (for example "035 - Base Price") into API-safe entity.
        const entityRow = filters.find(r => r.property === 'Entity');
        if (entityRow?.value) {
            this.currentEntity = this.normalizeEntityName(entityRow.value);
        }
    }

    getSimulationFilters() {
        return this.currentFilters;
    }

    setSimulationValues(values: any[]) {
        this.simulationValues = values;
    }

    getSimulationValues() {
        return this.simulationValues;
    }

    setAppliedFilters(filters: any[]) {
        this.appliedFilters = filters;
    }

    getAppliedFilters() {
        return this.appliedFilters;
    }

    setEntity(entity: string) {
        this.currentEntity = this.normalizeEntityName(entity);
    }

    getEntity() {
        return this.currentEntity;
    }

    addNewSimulation(sim: any) {
        const current = this.newSimulationsSubject.value;
        this.newSimulationsSubject.next([...current, sim]);
    }

    updateSimulationByName(name: string, updates: any) {
        const current = this.newSimulationsSubject.value;
        const updated = current.map(sim => {
            if (sim.name === name) {
                return { ...sim, ...updates };
            }
            return sim;
        });
        this.newSimulationsSubject.next(updated);
    }

    getNewSimulations() {
        return this.newSimulationsSubject.value;
    }

    setLastBusinessId(id: string) {
        this.lastBusinessId = id;
    }

    getLastBusinessId(): string {
        return this.lastBusinessId;
    }

    getLastRunUuid(): string {
        return this.lastRunUuid;
    }

    getLastRunAppliedFilters(): any[] {
        return this.cloneAppliedFilters(this.lastRunAppliedFilters);
    }

    getDeepDiveFilterContext() {
        return this.deepDiveFilterContextSubject.value;
    }

    setDeepDiveDetailsResponse(response: any) {
        this.deepDiveDetailsResponseSubject.next(response ?? null);
    }

    getDeepDiveDetailsResponse() {
        return this.deepDiveDetailsResponseSubject.value;
    }

    private toStringArray(value: any): string[] {
        if (!Array.isArray(value)) {
            return [];
        }
        return value.map(item => String(item).trim());
    }

    private formatCurrencySummaryValue(value: any, fallback: string = '$ 0'): string {
        const rawValue = String(value ?? '').trim();
        if (!rawValue || rawValue === '-' || rawValue === '$ -') {
            return fallback;
        }

        if (rawValue.startsWith('$')) {
            const normalizedCurrency = rawValue.replace(/^\$\s*/, '').trim();
            const parsedCurrency = Number(normalizedCurrency.replace(/[^0-9.-]/g, ''));
            if (!Number.isFinite(parsedCurrency)) {
                return fallback;
            }
            return rawValue;
        }

        const numericValue = Number(rawValue.replace(/[^0-9.-]/g, ''));
        if (!Number.isFinite(numericValue)) {
            return fallback;
        }

        return `$ ${formatDecimalWithCommas(numericValue)}`;
    }

    private extractDeepDiveFilterContext(response: any, summaryObj: any, summary: any) {
        const contextCandidates = [
            response?.requestData,
            response?.responseList?.[0]?.requestData,
            summary,
            summaryObj,
            summaryObj?.data,
            response?.responseList?.[0]?.summary?.[0],
            response?.responseList?.[0]?.summary?.[0]?.data,
            response?.responseList?.[0],
            response?.data,
            response
        ];

        let partNumberList: string[] = [];
        let regionList: string[] = [];

        for (const candidate of contextCandidates) {
            if (partNumberList.length === 0) {
                partNumberList = this.toStringArray(candidate?.part_number_list);
            }
            if (regionList.length === 0) {
                regionList = this.toStringArray(candidate?.region_list);
            }
            if (partNumberList.length > 0 && regionList.length > 0) {
                break;
            }
        }

        return {
            part_number_list: partNumberList,
            region_list: regionList
        };
    }

    private normalizeAppliedFiltersForDeepDive(filters: any[]): Array<{ field: string; operator: string; value: string | string[] }> {
        if (!Array.isArray(filters)) {
            return [];
        }

        return filters
            .map((filter: any) => {
                const field = String(filter?.attributeName || filter?.field || '').trim();
                const operator = String(filter?.operator || '=').trim() || '=';
                const rawValues = Array.isArray(filter?.attributeValues)
                    ? filter.attributeValues
                    : [filter?.value ?? ''];
                const normalizedValues = rawValues
                    .map((rawValue: any) => String(rawValue ?? '').trim())
                    .filter((value: string) => value !== '');
                const value = (filter?.multiValueInput === true || this.isListOperator(operator))
                    ? normalizedValues
                    : (normalizedValues.length > 1
                        ? normalizedValues
                        : (normalizedValues[0] ?? ''));

                return { field, operator, value };
            })
            .filter((filter: { field: string; value: string | string[] }) =>
                filter.field !== ''
                && (Array.isArray(filter.value) ? filter.value.length > 0 : filter.value !== '')
            );
    }

    private isListOperator(operator: any): boolean {
        const op = String(operator || '').trim().toLowerCase();
        return op === 'in' || op === 'not_in' || op === 'not in';
    }

    private cloneAppliedFilters(filters: any[]): any[] {
        if (!Array.isArray(filters)) {
            return [];
        }

        return filters.map((filter: any) => ({
            ...filter,
            value: Array.isArray(filter?.value) ? [...filter.value] : filter?.value,
            attributeValues: Array.isArray(filter?.attributeValues) ? [...filter.attributeValues] : filter?.attributeValues
        }));
    }

    /**
     * Run simulation using the TotalParts API
     * @param inputs Array of { property, value } from the simulation-values table
     */
    runSimulation(inputs: any[], uuid?: string): Observable<any> {
        const typeRow = inputs.find(r => r.property === 'Price Change Type');
        const valueRow = inputs.find(r => r.property === 'Price Change Value');
        const basedOnRow = inputs.find(r => r.property === 'Based on');

        const rawType = typeRow?.value || 'Percentage';
        const rawBasedOn = basedOnRow?.value || 'current_price';

        // Normalize values for backend (e.g., 'Percentage' -> 'percentage', 'Current Price' -> 'current_price')
        const priceChangeType = rawType.toLowerCase();
        const basedOn = rawBasedOn.toLowerCase().replace(/ /g, '_');

        const filters = [...(this.appliedFilters || [])];
        const normalizedScopedFilters = this.normalizeScopedAppliedFilters(filters);
        const deepDiveAppliedFilters = normalizedScopedFilters.length > 0
            ? normalizedScopedFilters
            : this.normalizeAppliedFiltersForDeepDive(filters);

        const resolvedUuid = String(uuid || '').trim();
        this.lastRunUuid = resolvedUuid;
        const requestData = {
            entity: this.currentEntity || 'dn_pricing',
            filter: {
                where: filters
            },
            ...(normalizedScopedFilters.length > 0 ? { appliedFilters: normalizedScopedFilters } : {}),
            priceChangeType: priceChangeType,
            priceChangeValue: parseFloat(String(valueRow?.value || '0').replace(/[^0-9.-]/g, '')) || 0,
            basedOn: basedOn,
            ...(resolvedUuid ? { uuid: resolvedUuid } : {})
        };

        this.logRunSaveAppliedFilters('SimulationService.runSimulation.beforePriceFileServiceRun', {
            resolvedUuid,
            currentEntity: this.currentEntity,
            rawAppliedFilters: filters,
            normalizedScopedFilters,
            deepDiveAppliedFilters,
            requestData
        });

        console.log('[SimulationService] runSimulation requestData:', JSON.stringify(requestData, null, 2));

        // Reset context before API call so stale values are not reused across runs.
        this.deepDiveFilterContextSubject.next({ part_number_list: [], region_list: [] });

        const runRequest$ = this.pfss.runSimulation(requestData);
        this.lastRunAppliedFilters = this.cloneAppliedFilters(this.pfss.getLastRunAppliedFilters());
        const runAppliedFilters = this.lastRunAppliedFilters.length > 0
            ? this.cloneAppliedFilters(this.lastRunAppliedFilters)
            : deepDiveAppliedFilters;
        this.logRunSaveAppliedFilters('SimulationService.runSimulation.afterPriceFileServiceRunObservableCreated', {
            resolvedUuid,
            priceFileServiceLastRunAppliedFilters: this.pfss.getLastRunAppliedFilters(),
            simulationServiceCachedLastRunAppliedFilters: this.lastRunAppliedFilters,
            runAppliedFilters
        });

        return runRequest$.pipe(
            map((response: any) => {
                console.log('[SimulationService] TotalParts raw response:', JSON.stringify(response, null, 2));
                const responseAppliedFilters = Array.isArray(response?.requestData?.appliedFilters)
                    ? response.requestData.appliedFilters
                    : [];
                this.lastRunAppliedFilters = this.cloneAppliedFilters(runAppliedFilters);
                this.logRunSaveAppliedFilters('SimulationService.runSimulation.responseMapped', {
                    resolvedUuid,
                    runAppliedFilters,
                    responseAppliedFilters,
                    simulationServiceCachedLastRunAppliedFilters: this.lastRunAppliedFilters,
                    responseRequestData: response?.requestData
                });
                this.setDeepDiveDetailsResponse({
                    ...(response || {}),
                    requestData: {
                        ...(response?.requestData || {}),
                        entity: this.currentEntity || 'dn_pricing',
                        source_entity: this.currentEntity || 'dn_pricing',
                        ...(resolvedUuid ? { uuid: resolvedUuid } : {}),
                        appliedFilters: runAppliedFilters
                    }
                });

                // Capture businessId from response for use during save
                if (response?.businessId) {
                    this.lastBusinessId = response.businessId;
                }

                // Handle error in response
                if (response?.error) {
                    console.error('Simulation API Error:', response.error);
                    return { success: false, error: response.error };
                }

                // Try multiple response formats:
                // Standard format: response.summary[0] or response.data.summary[0]
                // Endpoints format: response.responseList[0].summary[0]
                let summaryObj: any = null;

                if (response?.responseList?.[0]?.summary?.[0]) {
                    summaryObj = response.responseList[0].summary[0];
                } else if (response?.summary?.[0]) {
                    summaryObj = response.summary[0];
                } else if (response?.data?.summary?.[0]) {
                    summaryObj = response.data.summary[0];
                } else if (response?.responseList?.[0] && typeof response.responseList[0] !== 'string') {
                    summaryObj = response.responseList[0];
                }

                if (!summaryObj) {
                    const errorMsg = response?.responseList?.[0] || 'No summary data returned';
                    console.error('Simulation API Error: No summary found. Response:', response);
                    return { success: false, error: typeof errorMsg === 'string' ? errorMsg : 'No summary data' };
                }

                // The API nests the actual values inside a "data" property (e.g. summary[0].data.totalParts)
                // If we find summary[0].data, use that; otherwise use summaryObj directly
                const summary = summaryObj.data ?? summaryObj;
                const deepDiveFilterContext = this.extractDeepDiveFilterContext(response, summaryObj, summary);
                this.deepDiveFilterContextSubject.next(deepDiveFilterContext);

                const updates = [
                    // Part details
                    { metric: 'Total # Records', value: formatWholeNumberWithCommas(summary.totalParts ?? 0) },
                    { metric: '# Parts Impacted', value: formatWholeNumberWithCommas(summary.totalPartsImpacted ?? 0) },
                    // Current Status
                    { metric: 'Current Annual Units', value: formatWholeNumberWithCommas(summary.totalCurrentAnnualUnits ?? 0) },
                    { metric: 'Current Sales', value: summary.totalCurrentSalesValue ?? `$ 0.00` },
                    { metric: 'Current Future Sales', value: this.formatCurrencySummaryValue(summary.totalCurrentFutureSales, '$ 0.00') },
                    { metric: 'Current Impact (USD)', value: this.formatCurrencySummaryValue(summary.totalCurrentImpact, '$ 0.00') },
                    { metric: 'Current Impact %', value: summary.currentImpactPercent ?? '0.00%' },
                    // Simulated
                    { metric: 'Simulated Future Sales', value: this.formatCurrencySummaryValue(summary.simulatedFutureSales, '$ 0.00') },
                    { metric: 'Simulated Impact (USD)', value: this.formatCurrencySummaryValue(summary.simulatedImpactUSD, '$ 0.00') },
                    { metric: 'Simulated Impact %', value: summary.simulatedImpactPercent ?? summary.totalImpactPercent ?? '0.00%' },
                    // Summary
                    { metric: 'Total Impact (USD)', value: this.formatCurrencySummaryValue(summary.totalDollarImpact, '$ 0.00') },
                    { metric: 'Total Impact %', value: summary.totalImpactPercent ?? '0.00%' }
                ];

                this.simulationResultsSubject.next(updates);
                return { success: true, updates, deepDiveFilterContext };
            })
        );
    }

    private normalizeScopedAppliedFilters(filters: any[]): any[] {
        if (!Array.isArray(filters)) {
            return [];
        }

        const normalized = filters
            .map((filter: any, index: number, all: any[]) => {
                const field = String(filter?.field || filter?.attributeName || '').trim();
                if (!field) {
                    return null;
                }

                const operator = String(filter?.operator || '=').trim();
                const value = filter?.value ?? filter?.attributeValues ?? '';
                const rawLogical = String(filter?.logicalOperator || '').trim().toUpperCase();
                const logicalOperator = index === all.length - 1
                    ? ''
                    : (rawLogical === 'OR' ? 'OR' : 'AND');

                return {
                    field,
                    operator,
                    value,
                    logicalOperator,
                    beginScope: String(filter?.beginScope || ''),
                    endScope: String(filter?.endScope || '')
                };
            })
            .filter((item: any) => item !== null);

        return normalized;
    }
}
